import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { confirmProgramEnrollmentCheckout } from "../apis/programsApi";
import {
  hasCheckoutOpener,
  notifyProgramEnrollmentConfirmed,
  returnToCheckoutOpener,
} from "../utils/programEnrollmentEvents";
import { trackSpotifyPurchase } from "../utils/loadSpotifyAds";

const STAFF_USER_TYPES = new Set(["admin", "employee", "employee_admin", "coach_admin", "coach", "coach_employee"]);

const ProgramSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<"loading" | "success" | "error" | "returning">("loading");
  const [message, setMessage] = useState("");
  const userType = localStorage.getItem("userType") || "";
  const isStaff = STAFF_USER_TYPES.has(userType);
  const openedFromCheckoutPopup = hasCheckoutOpener();

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) {
      setStatus("error");
      setMessage(
        "Missing checkout session. Your payment may have succeeded, but enrollment could not be confirmed."
      );
      return;
    }

    confirmProgramEnrollmentCheckout(sessionId)
      .then((enrollment) => {
        if (!isStaff) {
          const amount = Number(enrollment?.totalAmount);
          trackSpotifyPurchase({
            value: Number.isFinite(amount) ? amount : undefined,
            orderId: sessionId,
          });
        }
        const programSessionId = enrollment.session?.id || enrollment.sessionId;
        if (openedFromCheckoutPopup && programSessionId) {
          setStatus("returning");
          returnToCheckoutOpener(programSessionId);
          window.setTimeout(() => {
            if (!window.closed) {
              setStatus("success");
            }
          }, 1500);
          return;
        }
        if (programSessionId) {
          notifyProgramEnrollmentConfirmed(programSessionId);
        }
        setStatus("success");
      })
      .catch((err: any) => {
        if (openedFromCheckoutPopup) {
          try {
            window.opener?.focus();
          } catch {
            // ignore focus errors in restrictive browsers
          }
        }
        setStatus("error");
        setMessage(err.response?.data?.message || "Failed to confirm enrollment. Please contact support.");
      });
  }, [searchParams, openedFromCheckoutPopup, isStaff]);

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: "center" }}>
        {status === "loading" && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <CircularProgress />
            <Typography variant="h6">Confirming your enrollment...</Typography>
            <Typography variant="body2" color="text.secondary">
              {openedFromCheckoutPopup
                ? "You will be returned to the previous tab shortly."
                : "Please keep this page open while we finalize your registration."}
            </Typography>
          </Box>
        )}

        {status === "returning" && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 72, color: "success.main" }} />
            <Typography variant="h6">Payment confirmed</Typography>
            <Typography variant="body2" color="text.secondary">
              Returning to your enrollments page...
            </Typography>
          </Box>
        )}

        {status === "success" && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 72, color: "success.main" }} />
            <Typography variant="h4" component="h1">
              Registration Confirmed
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {openedFromCheckoutPopup
                ? "Payment successful. You can close this tab and return to enrollments."
                : "Thank you. Your payment was successful and your spot is confirmed."}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, justifyContent: "center", mt: 2 }}>
              {isStaff ? (
                <Button component={Link} to="/admin/manage-program-enrollments" variant="contained">
                  Back to enrollments
                </Button>
              ) : (
                <Button component={Link} to="/dashboard" variant="contained">
                  View my enrollments
                </Button>
              )}
              <Button component={Link} to="/programs" variant="outlined">
                Browse programs
              </Button>
            </Box>
          </Box>
        )}

        {status === "error" && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <ErrorOutlineIcon sx={{ fontSize: 72, color: "error.main" }} />
            <Typography variant="h5" component="h1">
              Confirmation issue
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {message}
            </Typography>
            {isStaff ? (
              <Button component={Link} to="/admin/manage-program-enrollments" variant="contained" sx={{ mt: 1 }}>
                Back to enrollments
              </Button>
            ) : (
              <Button component={Link} to="/dashboard" variant="contained" sx={{ mt: 1 }}>
                Go to dashboard
              </Button>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default ProgramSuccess;
